/**
 * Device hub: owns the paired devices (trainer, HR, power meter, Click), merges their data,
 * keeps reconnecting on link loss (the ride keeps running), and publishes status to the UI store.
 */
import type { BleDevice, DeviceRole } from '@/ble/types';
import { Reconnector } from '@/ble/reconnect';
import { findPermittedDevice, forget, getRemembered, remember } from '@/ble/registry';
import { requestRealDevice } from '@/ble/transport';
import { SIM_MODE } from '@/env';
import { useDevices, type HubRole, type LiveData } from '@/store/devices';
import { getSettings, useSettings } from '@/store/settings';
import type { Settings } from '@/store/settingsModel';
import { BIKE } from '@/physics/bike';
import { TrainerController } from '@/trainer/controller';
import { Emitter, type AnyTrainer, type TrainerData } from '@/trainer/types';
import { CpsSensor } from './cps/CpsSensor';
import { HrSensor } from './hr/HrSensor';
import { createTrainer, NoControllableTrainerError } from './detect';
import { findSimDevice, getSimDevice } from './sim/simDevices';
import type { ButtonEvent, ConnectableController } from './click/types';

type Sensor = HrSensor | CpsSensor;

type UserConfig = { riderKg: number; bikeKg: number; wheelCircumferenceM: number };

function userConfigOf(s: Settings): UserConfig {
  return { riderKg: s.riderWeightKg, bikeKg: BIKE.massKg, wheelCircumferenceM: BIKE.wheelCircumferenceM };
}
type ClickFactory = (device: BleDevice) => ConnectableController;

const STALE_MS = 3000;

export class DeviceHub {
  trainer: AnyTrainer | null = null;
  hr: HrSensor | null = null;
  power: CpsSensor | null = null;
  click: ConnectableController | null = null;
  readonly controller = new TrainerController();
  readonly buttons = new Emitter<ButtonEvent>();
  readonly trainerLog = new Emitter<string>();
  private clickFactory: ClickFactory | null = null;
  private readonly reconnectors: Partial<Record<HubRole, Reconnector>> = {};
  private readonly cleanups: Partial<Record<HubRole, (() => void)[]>> = {};
  private trainerData: TrainerData = { t: 0 };
  private hrData: TrainerData = { t: 0 };
  private powerData: TrainerData = { t: 0 };
  private liveTimer: ReturnType<typeof setInterval> | null = null;

  setClickFactory(f: ClickFactory | null): void {
    this.clickFactory = f;
  }

  get clickAvailable(): boolean {
    return this.clickFactory !== null;
  }

  /** Merged live values. Power/cadence: trainer → power meter. HR: strap → trainer. */
  live(now = Date.now()): LiveData {
    const fresh = (d: TrainerData) => now - d.t < STALE_MS;
    const tr = fresh(this.trainerData) ? this.trainerData : null;
    const pm = fresh(this.powerData) ? this.powerData : null;
    const hr = fresh(this.hrData) ? this.hrData : null;
    return {
      t: now,
      powerW: tr?.powerW ?? pm?.powerW ?? 0,
      cadenceRpm: tr?.cadenceRpm ?? pm?.cadenceRpm ?? 0,
      hrBpm: hr?.hrBpm ?? tr?.hrBpm ?? 0,
      speedKmh: tr?.speedKmh ?? 0,
    };
  }

  /** Show the chooser (or pick the simulator) and connect. */
  async pair(role: HubRole, opts: { sim?: boolean } = {}): Promise<void> {
    const sim = opts.sim ?? SIM_MODE;
    const device = sim ? (getSimDevice(role) as BleDevice) : await requestRealDevice(role as DeviceRole);
    await this.attach(role, device);
  }

  async attach(role: HubRole, device: BleDevice): Promise<void> {
    await this.detach(role, false);
    const store = useDevices.getState();
    store.setStatus(role, {
      state: 'connecting',
      name: device.name ?? '',
      simulated: !!device.simulated,
      id: device.id,
      error: undefined,
    });
    try {
      const cleanups: (() => void)[] = [];
      if (role === 'trainer') {
        const t = await createTrainer(device);
        this.trainer = t;
        cleanups.push(t.onData((d) => (this.trainerData = d)));
        if (t.onLog) cleanups.push(t.onLog((l) => this.trainerLog.emit(l)));
        if (t.setUserConfig) {
          // FE-C page 55 / Wahoo sim init take rider + bike weight; sent on every (re)connect by the trainer.
          await t.setUserConfig(userConfigOf(getSettings()));
          cleanups.push(
            useSettings.subscribe((st, prev) => {
              const a = userConfigOf(st.settings);
              const b = userConfigOf(prev.settings);
              if (a.riderKg !== b.riderKg) void t.setUserConfig?.(a);
            }),
          );
        }
        cleanups.push(this.wireConnection(role, t));
        await t.connect();
        this.controller.attach(t);
        store.setStatus(role, { state: 'connected', kind: t.kind, caps: t.caps, name: t.name });
      } else if (role === 'hr' || role === 'power') {
        const s: Sensor = role === 'hr' ? new HrSensor(device) : new CpsSensor(device);
        if (role === 'hr') this.hr = s as HrSensor;
        else this.power = s as CpsSensor;
        cleanups.push(
          s.onData((d) => {
            if (role === 'hr') this.hrData = d;
            else this.powerData = d;
          }),
        );
        cleanups.push(this.wireConnection(role, s));
        await s.connect();
        store.setStatus(role, { state: 'connected', kind: role, battery: s.batteryPct });
        setTimeout(() => useDevices.getState().setStatus(role, { battery: s.batteryPct }), 1500);
      } else if (role === 'click') {
        if (!this.clickFactory) throw new Error('click-disabled');
        const c = this.clickFactory(device);
        this.click = c;
        cleanups.push(c.onButton((e) => this.buttons.emit(e)));
        cleanups.push(this.wireConnection(role, c));
        await c.connect();
        store.setStatus(role, { state: 'connected', kind: c.variant, battery: c.batteryPct });
        setTimeout(
          () => useDevices.getState().setStatus(role, { battery: c.batteryPct, kind: c.variant }),
          2000,
        );
      }
      this.cleanups[role] = cleanups;
      remember(role as DeviceRole, device);
      this.ensureLiveTimer();
    } catch (err) {
      const msg =
        err instanceof NoControllableTrainerError
          ? err.detected === 'power-only'
            ? 'Kein steuerbarer Trainer gefunden – als Leistungsmesser koppeln.'
            : 'Kein unterstützter Trainer (FTMS, FE-C oder Wahoo) gefunden.'
          : err instanceof Error
            ? err.message
            : String(err);
      await this.detach(role, false);
      useDevices.getState().setStatus(role, { state: 'error', error: msg, name: device.name ?? '' });
      throw err;
    }
  }

  private wireConnection(
    role: HubRole,
    dev: {
      onConnectionChange(cb: (c: boolean) => void): () => void;
      connect(): Promise<void>;
      wasManualDisconnect?: boolean;
    },
  ): () => void {
    return dev.onConnectionChange((connected) => {
      const store = useDevices.getState();
      if (connected) {
        this.reconnectors[role]?.stop();
        delete this.reconnectors[role];
        store.setStatus(role, { state: 'connected', attempt: 0 });
        return;
      }
      if (dev.wasManualDisconnect) return;
      store.setStatus(role, { state: 'reconnecting', attempt: 0 });
      const r = new Reconnector(() => dev.connect(), {
        onAttempt: (attempt) => useDevices.getState().setStatus(role, { state: 'reconnecting', attempt }),
      });
      this.reconnectors[role]?.stop();
      this.reconnectors[role] = r;
      r.start();
    });
  }

  async detach(role: HubRole, forgetDevice = true): Promise<void> {
    this.reconnectors[role]?.stop();
    delete this.reconnectors[role];
    for (const c of this.cleanups[role] ?? []) c();
    delete this.cleanups[role];
    const inst =
      role === 'trainer'
        ? this.trainer
        : role === 'hr'
          ? this.hr
          : role === 'power'
            ? this.power
            : this.click;
    if (inst) {
      try {
        await inst.disconnect();
      } catch {
        /* ignore */
      }
      (inst as { dispose?: () => void }).dispose?.();
    }
    if (role === 'trainer') {
      this.trainer = null;
      this.controller.attach(null);
      this.trainerData = { t: 0 };
    } else if (role === 'hr') {
      this.hr = null;
      this.hrData = { t: 0 };
    } else if (role === 'power') {
      this.power = null;
      this.powerData = { t: 0 };
    } else {
      this.click = null;
    }
    if (forgetDevice) forget(role as DeviceRole);
    useDevices.getState().setStatus(role, { state: 'none' });
  }

  /** Manual one-click reconnect (when the link is lost and auto-reconnect gave up / is waiting). */
  async reconnectNow(role: HubRole): Promise<void> {
    const inst =
      role === 'trainer'
        ? this.trainer
        : role === 'hr'
          ? this.hr
          : role === 'power'
            ? this.power
            : this.click;
    if (inst) {
      this.reconnectors[role]?.stop();
      delete this.reconnectors[role];
      useDevices.getState().setStatus(role, { state: 'connecting' });
      await inst.connect();
      return;
    }
    await this.autoReconnect(role);
  }

  /** Try to reconnect remembered devices without the chooser (simulator, or getDevices() when enabled). */
  async autoReconnect(role: HubRole): Promise<boolean> {
    const rem = getRemembered(role as DeviceRole);
    if (!rem) return false;
    let device: BleDevice | null;
    if (rem.simulated) {
      if (!SIM_MODE) return false;
      device = (findSimDevice(rem.id) as BleDevice | null) ?? null;
      if (!device) {
        try {
          device = getSimDevice(role) as BleDevice;
        } catch {
          device = null;
        }
      }
    } else {
      device = await findPermittedDevice(rem.id);
    }
    if (!device) return false;
    try {
      await this.attach(role, device);
      return true;
    } catch {
      return false;
    }
  }

  async autoReconnectAll(): Promise<void> {
    for (const role of ['trainer', 'hr', 'power', 'click'] as HubRole[]) {
      if (role === 'click' && !this.clickFactory) continue;
      await this.autoReconnect(role);
    }
  }

  private ensureLiveTimer(): void {
    if (this.liveTimer) return;
    this.liveTimer = setInterval(() => {
      useDevices.getState().setLive(this.live());
    }, 250);
  }
}

export const hub = new DeviceHub();
